import type { ReactNode } from "react";
import { ClaimList, ElementList } from "@/components/patterns/ThesisLists";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { DisclosureHeader, useDisclosure } from "@/components/ui/Disclosure";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { Spinner } from "@/components/ui/Spinner";
import { useClaimStrategy } from "@/hooks/use-claim-strategy";
import { useInventionElements } from "@/hooks/use-invention";

/** A list once its read answers: the words for still reading, or for a read that failed. */
function Read<T>({
  query,
  failed,
  children,
}: {
  query: { data: T | undefined; isError: boolean; error: unknown };
  failed: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isError)
    return <LoadFailure error={query.error} fallback={failed} />;
  if (query.data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size="sm" /> Reading…
      </p>
    );
  }
  return children(query.data);
}

/**
 * What every section of the plan must support, folded by default: the elements of the
 * invention and the claim set, as the attorney confirmed them.
 */
export function ThesisCard({ taskId }: { taskId: string }) {
  const fold = useDisclosure(false);
  const invention = useInventionElements(taskId);
  const claims = useClaimStrategy(taskId);
  return (
    <Panel shape="card" pad="none" className="mt-6">
      <DisclosureHeader
        trigger={fold.trigger}
        open={fold.open}
        className="px-3.5 py-2.5"
        title={
          <span className="text-sm font-semibold">
            Show <em>Elements of the Invention</em> &amp; <em>Claims</em>
          </span>
        }
        meta={
          <span className="text-xs text-muted-foreground">
            what every section plan below must support
          </span>
        }
      />
      <div
        {...fold.panel}
        className="grid gap-6 border-t border-border px-3.5 py-3 md:grid-cols-3"
      >
        <div>
          <MicroLabel>Elements of the Invention</MicroLabel>
          <Read
            query={invention}
            failed="The elements of the invention could not be loaded."
          >
            {(data) => <ElementList elements={data.elements} />}
          </Read>
        </div>
        <div className="md:col-span-2">
          <MicroLabel>Claims</MicroLabel>
          <Read query={claims} failed="The claims could not be loaded.">
            {(data) => <ClaimList claims={data.claims} />}
          </Read>
        </div>
      </div>
    </Panel>
  );
}
